import React, { useState } from 'react';

const initials = (name = '') => name.split(' ').filter(Boolean).slice(0, 2).map(p => p[0].toUpperCase()).join('') || '?';

/**
 * Premium customer dashboard top bar.
 */
const CustomerTopBar = ({
  user,
  pageTitle = 'Investor Portal',
  pageSubtitle = 'Value Plus GmbH',
  notificationCount = 0,
  activeGrantors = [],
  viewingGrantorId,
  onSwitchGrantor,
  onMenu,
  showSuccessBadge = false,
}) => {
  const [menuOpen, setMenuOpen] = useState(false);
  return (
    <header
      className="h-16 sm:h-20 px-4 sm:px-8 flex items-center justify-between gap-3 bg-white border-b border-gray-100"
      data-testid="customer-topbar"
    >
      <div className="flex items-center gap-3 flex-1 min-w-0">
        {onMenu && (
          <button
            onClick={onMenu}
            className="lg:hidden -ml-1 p-2 rounded-md hover:bg-gray-100"
            data-testid="topbar-menu-button"
            aria-label="Menü öffnen"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
        )}
        <div className="min-w-0">
          <h1 className="font-heading text-lg sm:text-2xl text-[#0A0A0A] leading-tight truncate">{pageTitle}</h1>
          <p className="text-[11px] sm:text-xs text-gray-500 truncate">{pageSubtitle}</p>
        </div>
      </div>

      <div className="flex items-center gap-2 sm:gap-4">
        {activeGrantors.length > 0 && (
          <select
            value={viewingGrantorId || 'self'}
            onChange={(e) => onSwitchGrantor?.(e.target.value === 'self' ? null : parseInt(e.target.value, 10))}
            className="hidden sm:block text-xs border border-gray-200 rounded-lg px-3 py-2 bg-white hover:border-gray-300 cursor-pointer"
            data-testid="account-switcher"
          >
            <option value="self">Mein Konto</option>
            {activeGrantors.map((a) => (
              <option key={a.id} value={a.grantor_user_id}>
                {a.grantor_full_name} (Vollmacht)
              </option>
            ))}
          </select>
        )}

        <button className="relative p-2 rounded-full hover:bg-gray-100 transition-colors vp-tactile" data-testid="topbar-notification" aria-label="Benachrichtigungen">
          <svg className="w-5 h-5 text-gray-700 transition-transform group-hover:rotate-12" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.6}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
          </svg>
          {notificationCount > 0 && (
            <span className="absolute top-1 right-1 min-w-[16px] h-4 px-1 rounded-full bg-[#C5A059] text-[9px] text-[#0A0A0A] font-bold flex items-center justify-center vp-anim-pulse-gold" data-testid="topbar-notification-count">
              {notificationCount > 99 ? '99+' : notificationCount}
            </span>
          )}
        </button>

        <div className="relative">
          <button
            onClick={() => setMenuOpen((s) => !s)}
            className="vp-tactile flex items-center gap-2 sm:gap-3 px-1.5 sm:px-2 py-1 rounded-lg hover:bg-gray-50 transition-colors"
            data-testid="topbar-user-button"
          >
            <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-[#0A0A0A] text-white flex items-center justify-center font-medium text-sm ring-2 ring-transparent transition-all duration-300 hover:ring-[#C5A059]/30">
              {initials(user?.name)}
            </div>
            <div className="hidden md:block text-left">
              <p className="text-sm font-medium text-[#0A0A0A] leading-tight">{user?.name || 'Investor'}</p>
              <p className="text-xs text-gray-500 leading-tight">{user?.email}</p>
            </div>
            <svg className="hidden md:block w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
            </svg>
          </button>
          {menuOpen && (
            <>
              <div className="fixed inset-0 z-10" onClick={() => setMenuOpen(false)} />
              <div className="absolute right-0 mt-2 w-60 rounded-xl bg-white shadow-lg border border-gray-100 z-20 overflow-hidden vp-anim-scale origin-top-right" data-testid="topbar-user-menu">
                <div className="p-4 border-b border-gray-100">
                  <p className="text-sm font-medium">{user?.name}</p>
                  <p className="text-xs text-gray-500 mt-0.5">{user?.email}</p>
                </div>
                {activeGrantors.length > 0 && (
                  <div className="p-3 border-b border-gray-100 sm:hidden">
                    <p className="text-[10px] uppercase tracking-wider text-gray-400 mb-1.5">Konto wechseln</p>
                    <select
                      value={viewingGrantorId || 'self'}
                      onChange={(e) => { onSwitchGrantor?.(e.target.value === 'self' ? null : parseInt(e.target.value, 10)); setMenuOpen(false); }}
                      className="w-full text-xs border border-gray-200 rounded-lg px-2 py-1.5 bg-white"
                      data-testid="account-switcher-mobile"
                    >
                      <option value="self">Mein Konto</option>
                      {activeGrantors.map((a) => (
                        <option key={a.id} value={a.grantor_user_id}>
                          {a.grantor_full_name} (Vollmacht)
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        {showSuccessBadge && (
          <div className="hidden xl:flex items-center gap-2 px-3 py-1.5 rounded-lg bg-emerald-50 border border-emerald-100 animate-in fade-in" data-testid="topbar-success-badge">
            <svg className="w-4 h-4 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span className="text-xs text-emerald-700 font-medium">Erfolgreich angemeldet</span>
          </div>
        )}
      </div>
    </header>
  );
};

export default CustomerTopBar;
